<script setup lang="ts">
import SiteSection from '@/client/components/SiteSection.vue';
import type { ProductsContent } from '@/client/types';
import { Link } from '@inertiajs/vue3';
import { ArrowRight, Boxes } from 'lucide-vue-next';

/**
 * The products the studio sells, from the catalog: a card each, leading to its page.
 */
defineProps<{
    id: string;
    content: ProductsContent;
    products: App.Data.SiteProductData[];
}>();
</script>

<template>
    <SiteSection v-if="products.length" :id="id" :title="content.title" :subtitle="content.subtitle">
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Link
                v-for="product in products"
                :key="product.code"
                :href="product.url"
                class="group grid content-start overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md"
            >
                <img v-if="product.image_url" :src="product.image_url" :alt="product.title" class="aspect-[4/3] w-full object-cover" loading="lazy" />
                <div v-else class="grid aspect-[4/3] w-full place-items-center bg-muted text-muted-foreground">
                    <Boxes class="size-10" />
                </div>
                <div class="grid gap-2 p-5">
                    <h3 class="text-xl font-semibold tracking-tight">{{ product.title }}</h3>
                    <p v-if="product.summary" class="text-sm leading-relaxed text-muted-foreground">{{ product.summary }}</p>
                    <span class="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary">
                        {{ $t('site.product_more') }}
                        <ArrowRight class="size-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                </div>
            </Link>
        </div>
    </SiteSection>
</template>
